import { VueConstructor } from 'vue/types/umd'
import { Menu } from './menu/menu'
import { Search } from './search/search'
import { LauncherLink } from './httpSettings/LauncherLinks'
import { createLinkTransformer } from './httpSettings/launcherLinkFunctions'
import { BootState } from '@/boot'
import { BrowserNotifications } from './browserNotifications/browserNotifications'
import { UISettings } from './uiSettings/uiSettings'
import { User } from './definitions/User'

// This variable is being passed in from the "vue.config.js"
// It contains the "package.json version" in dev and production
declare const APPLICATION_VERSION: string

/**
 * Api layer for the Teds Launcher
 */
export class TedsLauncher {
  /**
   * Holds the view constructor that is using the this state
   */
  public vue!: VueConstructor

  /**
   * It contains the "package.json version" in dev and production
   */
  public version: string = APPLICATION_VERSION

  /**
   * Holds the instance of boot state
   */
  public bootState: BootState

  /**
   * Holds the instance of state for ui settings
   */
  public uiSettings: UISettings

  /**
   * Holds the instance of state for user
   */
  public user: User

  /**
   * Holds the instance for state pertaining to browser notifications
   */
  public browserNotifications = new BrowserNotifications()

  /**
   * Holds the state for the global search
   */
  public search: Search = new Search()

  /**
   * Holds the state for the slide out menu
   */
  public menu: Menu = new Menu()

  /**
   * Holds the list of links for MVP provided
   * by http-setting.json file
   */
  public links: LauncherLink[] = []

  /**
   * Constructs the instance of TedsLauncher
   * @param bootState
   */
  public constructor(bootState: BootState) {
    this.bootState = bootState
    this.uiSettings = new UISettings(bootState)
    this.user = bootState.user
    this.links = this.bootState.httpSettings.app.configuration.links.map(
      createLinkTransformer({ userID: bootState.user.personID })
    )
  }

  /**
   * Launcher hook to set up the state before loading the page
   */
  public async mount(): Promise<void> {
    await this.browserNotifications.getBrowserNotifications()
  }

  /**
   * Helper method to logout from the global state object
   */
  public logout(): void {
    this.bootState.auth?.logout()
  }
}

/**
 * Bind Teds Launcher to a Vue Constructor
 * @param Vue
 */
export async function createAndBindTedsLauncherToVue(Vue: VueConstructor, bootState: BootState): Promise<TedsLauncher> {
  if (Vue.prototype.$launcher instanceof TedsLauncher === false) {
    const launcher = new TedsLauncher(bootState)
    const reactiveLauncher = Vue.observable(launcher)
    reactiveLauncher.vue = Vue
    Vue.set(Vue.prototype, '$launcher', reactiveLauncher)
    return reactiveLauncher
  } else {
    throw new Error(`TEDS Launcher has already been attached to vue`)
  }
}

declare module 'vue/types/vue' {
  interface Vue {
    $launcher: TedsLauncher
  }
}
